<template>
  <v-menu
    v-model="menuOpen"
    min-width="40%"
    max-width="66%"
    aria-live="polite"
    aria-atomic="true"
    role="dialog"
    :attach="attachMenuTo"
    left
    nudge-left="2"
    :nudge-bottom="menuOffset"
    :close-on-content-click="false"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        id="cart-icon"
        color="white"
        v-bind="attrs"
        v-on="on"
        @click="toggleCart"
      >
        <v-badge
          :value="cartItemLength"
          :content="cartItemLength"
          class="d-flex flex-column"
          overlap
        >
          <v-icon
            :large="$vuetify.breakpoint.smAndDown"
            :x-large="$vuetify.breakpoint.mdAndUp"
            color="grey darken-2"
          >
            mdi-cart-outline
          </v-icon>
          <span
            class="text-capitalize subtitle-1 d-none d-md-block"
            v-text="$t('header-cart-title')"
          />
        </v-badge>
      </v-btn>
    </template>
    <v-card
      ref="menu"
      v-resize.quiet="onResize"
      class="cart-menu-card"
      :ripple="false"
      :max-height="menuHeight"
      width="100%"
    >
      <v-subheader class="grey--text text--darken-4">
        <span v-text="$t('header-cart-item-count')" />
        <span
          class="d-inline-block ml-1"
          v-text="`(${cartItemLength})`"
        />
      </v-subheader>
      <v-divider class="ma-0" />
      <div v-if="cartItems && cartItems.length">
        <v-list id="cart-items">
          <cart-menu-item
            v-for="cartItem in cartItems"
            :key="cartItem.id"
            :cart-item="cartItem"
            @edit-cart-item="editItem"
            @remove-cart-item="removeFromCart"
          />
        </v-list>
        <v-card-text
          id="cart-total"
          class="font-weight-bold py-2 text-body-1 text-right"
        >
          <span
            class="text-body-2"
            v-text="$t('complete-total')"
          />:
          <span
            class="font-weight-bold py-2 text-body-1"
            v-text="formatCartTotal"
          />
        </v-card-text>
        <v-card-actions class="mx-1">
          <v-btn
            id="checkout-button"
            block
            class="mb-3 mt-4"
            color="accent"
            large
            @click.stop="navigateToCheckout"
            @keyup.enter.prevent="navigateToCheckout"
            v-text="$t('global-button-checkout')"
          />
        </v-card-actions>
      </div>
      <v-card-text
        v-else
        id="cart-message-empty"
        class="text-center"
      >
        {{ $t('global-cart-no-items') }}
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import CartMenuItem from '@/components/cart/CartMenuItem'
import { ItemFeeMixin } from '@/components/shared/mixins/item-fee-mixin'
import { ItemFulfillmentMixin } from '@/components/shared/mixins/item-fulfillment-mixin'
import { mapActions, mapGetters } from 'vuex'
import {
  GET_CART_ITEMS,
  GET_CART_TOTAL,
  GET_CURRENCY_CODE,
  GET_SHOW_CART,
  ORDER_TRACKING_PROPERTIES
} from '../../store/get-types'
import { REMOVE_CART_ITEM, SET_SHOW_CART } from '@/store/action-types'
import { CART_VIEWED } from '@/plugins/segment/SegmentConstants'

export default {
  name: 'Cart2',
  components: {
    CartMenuItem
  },
  mixins: [ItemFeeMixin, ItemFulfillmentMixin],
  props: {
    attachMenuTo: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      windowHeight: window.innerHeight,
      menuOpen: false
    }
  },
  computed: {
    ...mapGetters({
      cartItems: GET_CART_ITEMS,
      cartCurrencyCode: GET_CURRENCY_CODE,
      cartTotal: GET_CART_TOTAL,
      showCart: GET_SHOW_CART,
      orderTrackingProperties: ORDER_TRACKING_PROPERTIES
    }),
    menuOffset () {
      return 100
    },
    cartItemLength () {
      return this.cartItems.length
    },
    menuHeight () {
      return this.windowHeight - this.menuOffset
    },
    formatCartTotal () {
      return this.$money(this.cartTotal, this.cartCurrencyCode)
    }
  },
  watch: {
    showCart: {
      handler (val) {
        this.menuOpen = val.isShown
      },
      deep: true
    },
    menuOpen (val) {
      this.setShowCart({ isShown: val, fadeOut: false })
    }
  },
  methods: {
    ...mapActions({
      removeFromCart: REMOVE_CART_ITEM,
      setShowCart: SET_SHOW_CART
    }),
    navigateToCheckout () {
      this.toggleCart()
      this.$router.push({ name: 'Checkout' })
    },
    editItem (cartItem) {
      // RL-2383 Emit captured in CatalogView.vue
      this.$root.$emit('set-selected-from-cart', cartItem.editData)
      this.removeFromCart(cartItem)
    },
    async toggleCart () {
      if (window.innerWidth <= 767) {
        this.$router.push({ name: 'Cart' })
      } else {
        await this.$nextTick()
        if (this.menuOpen) {
          this.$segment.track(CART_VIEWED, this.orderTrackingProperties)
        }
      }
    },
    onResize () {
      this.windowHeight = window.innerHeight
      this.setShowCart({ isShown: false, fadeOut: false })
    }
  }
}
</script>
<style lang="scss" scoped>
.cart-menu-card {
  overflow-y: auto;
  overflow-x: hidden;
}
#cart-icon {
  @media only screen and (max-width: 320px) {
    padding: 0;
    min-width: 52px;
  }
}
</style>
